import { minHourlyRate, Space } from '../../models/asset.model';
import { buildSpaceSeoDescription } from './seo.utils';

export interface SpaceJsonLdInput {
  space: Pick<
    Space,
    | 'name'
    | 'description'
    | 'district'
    | 'province'
    | 'postalCode'
    | 'country'
    | 'countryCode'
    | 'formattedAddress'
    | 'address'
    | 'latitude'
    | 'longitude'
    | 'phone'
    | 'website'
    | 'averageRating'
    | 'reviewCount'
    | 'pricePerHour'
    | 'pricing'
  >;
  canonicalUrl: string;
  locationLabel?: string;
  imageUrl?: string;
}

function cleanAddress(space: SpaceJsonLdInput['space']): string {
  for (const value of [space.formattedAddress, space.address]) {
    const trimmed = (value ?? '').trim();
    if (trimmed && trimmed.toLowerCase() !== 'undefined') return trimmed;
  }
  return '';
}

function countryCode(space: SpaceJsonLdInput['space']): string | undefined {
  const code = space.countryCode?.trim().toUpperCase();
  if (code && /^[A-Z]{2}$/.test(code)) return code;
  return undefined;
}

/** Schema.org JSON-LD for public space detail pages (Product + location + optional ratings). */
export function buildSpaceJsonLd(input: SpaceJsonLdInput): Record<string, unknown> {
  const { space, canonicalUrl, locationLabel, imageUrl } = input;
  const name = space.name?.trim() || 'Espacio';
  const description = buildSpaceSeoDescription(space.name, space.description ?? '', locationLabel);
  const minPrice = minHourlyRate(space);

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['Product', 'LocalBusiness'],
    name,
    description,
    url: canonicalUrl,
    brand: {
      '@type': 'Brand',
      name: 'TimeFlexRent',
    },
    offers: {
      '@type': 'Offer',
      url: canonicalUrl,
      priceCurrency: 'EUR',
      price: minPrice,
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: minPrice,
        priceCurrency: 'EUR',
        unitText: 'hora',
      },
    },
  };

  if (imageUrl?.trim()) {
    jsonLd['image'] = imageUrl.trim();
  }

  const street = cleanAddress(space);
  const locality = space.district?.trim() || space.province?.trim();
  const region = space.province?.trim();
  const postalCode = space.postalCode?.trim();
  const cc = countryCode(space);
  if (street || locality || region || postalCode || cc || space.country?.trim()) {
    jsonLd['address'] = {
      '@type': 'PostalAddress',
      ...(street ? { streetAddress: street } : {}),
      ...(locality ? { addressLocality: locality } : {}),
      ...(region ? { addressRegion: region } : {}),
      ...(postalCode ? { postalCode } : {}),
      ...(cc ? { addressCountry: cc } : space.country?.trim() ? { addressCountry: space.country.trim() } : {}),
    };
  }

  const lat = space.latitude;
  const lon = space.longitude;
  if (typeof lat === 'number' && typeof lon === 'number' && Number.isFinite(lat) && Number.isFinite(lon)) {
    jsonLd['geo'] = {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lon,
    };
  }

  const phone = space.phone?.trim();
  if (phone) jsonLd['telephone'] = phone;

  const website = space.website?.trim();
  if (website && /^https?:\/\//i.test(website)) {
    jsonLd['sameAs'] = [website];
  }

  const reviewCount = space.reviewCount;
  const rating = space.averageRating;
  if (
    typeof reviewCount === 'number'
    && reviewCount > 0
    && typeof rating === 'number'
    && Number.isFinite(rating)
  ) {
    jsonLd['aggregateRating'] = {
      '@type': 'AggregateRating',
      ratingValue: Math.round(rating * 10) / 10,
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return jsonLd;
}
